import { degToRad, forwardXZ, rightXZ, vNormalize } from '../sim/math.js';
import { capsuleOf } from '../sim/step-mech.js';
import { applyDamage, areaPacket } from './damage.js';
import { damageDeployable } from './deployable-damage.js';
import { groundBelow, spawnDeployable } from './deployables.js';
import { addEffect, applyImpulse, knockbackFrom, refreshEffect, removeEffectsOfKind, TOGGLE_TICKS, } from './effects.js';
import { fireBurstShot } from './effects-tick.js';
import { capsuleInCone, closestPointOnAxis } from './geometry.js';
import { aimDir, aimOrigin, centerOf, combatantCapsule, secondsToTicks } from './state.js';
import { defOf, emit, mech } from './tick.js';
import { otherSlot } from './types.js';
import { beamTick } from './weapons.js';
import { weaponOf } from './catalog.js';
export function modsToParams(mods) {
    const p = {};
    if (mods.speedScale !== undefined)
        p.speedScale = mods.speedScale;
    if (mods.turnScale !== undefined)
        p.turnScale = mods.turnScale;
    if (mods.damageScale !== undefined)
        p.damageScale = mods.damageScale;
    if (mods.damageTakenScale !== undefined)
        p.damageTakenScale = mods.damageTakenScale;
    if (mods.fireRateScale !== undefined)
        p.fireRateScale = mods.fireRateScale;
    if (mods.heatScale !== undefined)
        p.heatScale = mods.heatScale;
    if (mods.dissipationScale !== undefined)
        p.dissipationScale = mods.dissipationScale;
    if (mods.spreadScale !== undefined)
        p.spreadScale = mods.spreadScale;
    if (mods.armorBonus !== undefined)
        p.armorBonus = mods.armorBonus;
    if (mods.cooldownScale !== undefined)
        p.cooldownScale = mods.cooldownScale;
    if (mods.jetThrustScale !== undefined)
        p.jetThrustScale = mods.jetThrustScale;
    if (mods.jetCostScale !== undefined)
        p.jetCostScale = mods.jetCostScale;
    if (mods.flags)
        p.flags = [...mods.flags];
    return p;
}
/** Horizontal distance between two mechs' axes. */
function planarDistance(t, a, b) {
    const pa = mech(t, a).body.position;
    const pb = mech(t, b).body.position;
    return Math.hypot(pa.x - pb.x, pa.z - pb.z);
}
/** Line of sight from `slot`'s aim origin to the enemy's centre. */
export function hasLineOfSight(t, slot) {
    const enemy = otherSlot(slot);
    const target = mech(t, enemy);
    if (!target.alive)
        return false;
    const from = aimOrigin(mech(t, slot).body, defOf(t, slot), t.ctx.balance.aimHeightFraction);
    const to = centerOf(target.body, defOf(t, enemy));
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-6)
        return true;
    const hit = t.world.castRayHit(from, { x: dx / dist, y: dy / dist, z: dz / dist }, dist);
    return !hit || hit.distance >= dist - defOf(t, enemy).capsule.radius;
}
function dashDirection(t, slot, direction, target, input) {
    const c = mech(t, slot);
    const f = forwardXZ(c.body.aimYaw);
    if (direction === 'back')
        return { x: -f.x, z: -f.z };
    if (direction === 'move') {
        const r = rightXZ(c.body.aimYaw);
        const x = r.x * input.moveX + f.x * input.moveY;
        const z = r.z * input.moveX + f.z * input.moveY;
        const len = Math.hypot(x, z);
        if (len > 0.01)
            return { x: x / len, z: z / len };
        return { x: f.x, z: f.z };
    }
    if (direction === 'toTarget' && target) {
        const x = target.x - c.body.position.x;
        const z = target.z - c.body.position.z;
        const len = Math.hypot(x, z);
        if (len > 0.01)
            return { x: x / len, z: z / len };
    }
    return { x: f.x, z: f.z };
}
const scratchMove = {
    x: 0,
    y: 0,
    z: 0,
    grounded: false,
    hitWall: false,
    hitCeiling: false,
};
function runDash(t, slot, p, target, input) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const dir = dashDirection(t, slot, p.direction, target, input);
    let distance = p.distance;
    if (p.direction === 'toTarget' && target) {
        const enemy = otherSlot(slot);
        const gap = planarDistance(t, slot, enemy) - def.capsule.radius - defOf(t, enemy).capsule.radius - 0.4;
        distance = Math.max(0, Math.min(distance, gap));
    }
    if (p.invulnerableSeconds > 0) {
        addEffect(t, slot, 'invulnerable', slot, secondsToTicks(p.invulnerableSeconds), {
            flags: ['invulnerable'],
        });
    }
    if (p.mode === 'blink') {
        t.world.moveCapsule(c.body.position, { x: dir.x * distance, y: 0, z: dir.z * distance }, capsuleOf(def), scratchMove);
        c.body.position = { x: scratchMove.x, y: scratchMove.y, z: scratchMove.z };
        return;
    }
    const speed = distance / p.seconds;
    addEffect(t, slot, 'dash', slot, secondsToTicks(p.seconds), {
        dash: {
            vx: dir.x * speed,
            vz: dir.z * speed,
            damage: p.damage,
            knockback: p.knockback,
            stopOnHit: p.stopOnHit,
            hit: false,
        },
        ...(p.ccImmune ? { flags: ['ccImmune'] } : {}),
    });
}
function runDeploy(t, slot, p, target) {
    const c = mech(t, slot);
    const f = forwardXZ(c.body.aimYaw);
    const base = p.atTarget && target
        ? { x: target.x, y: target.y, z: target.z }
        : {
            x: c.body.position.x + f.x * p.forward,
            y: c.body.position.y,
            z: c.body.position.z + f.z * p.forward,
        };
    for (let i = 0; i < p.count; i++) {
        const angle = (i / p.count) * Math.PI * 2;
        const pos = p.count > 1
            ? { x: base.x + Math.cos(angle) * 2.6, y: base.y, z: base.z + Math.sin(angle) * 2.6 }
            : { ...base };
        if (p.kind !== 'drone')
            pos.y = groundBelow(t, { x: pos.x, y: pos.y + 0.5, z: pos.z });
        const d = spawnDeployable(t, slot, p.kind, pos, c.body.aimYaw, p.hp, p.seconds, {
            ...(p.halfExtents ? { halfExtents: { ...p.halfExtents } } : {}),
            ...(p.weapon !== undefined ? { weapon: p.weapon } : {}),
            ...(p.range !== undefined ? { range: p.range } : {}),
            ...(p.hpPerSecond !== undefined ? { hpPerSecond: p.hpPerSecond } : {}),
            ...(p.pulse ? { pulse: { ...p.pulse } } : {}),
            ...(p.detonate ? { detonate: { ...p.detonate } } : {}),
        });
        if (p.kind === 'turret')
            d.timer = 0.5;
    }
}
/** Slots a self / enemy / radius targeting resolves to (radius = the enemy when within it). */
function targetsOf(t, slot, target, radius, lineOfSight) {
    if (target === 'self')
        return [slot];
    const enemy = otherSlot(slot);
    if (!mech(t, enemy).alive)
        return [];
    if (target === 'radius' && radius !== undefined && planarDistance(t, slot, enemy) > radius)
        return [];
    if (lineOfSight && !hasLineOfSight(t, slot))
        return [];
    return [enemy];
}
function runBuff(t, slot, p) {
    for (const target of targetsOf(t, slot, p.target, p.radius, p.lineOfSight)) {
        refreshEffect(t, target, p.kind, slot, secondsToTicks(p.seconds), modsToParams(p.mods));
    }
}
function runAoe(t, slot, p, target) {
    const self = mech(t, slot);
    const def = defOf(t, slot);
    const origin = p.origin === 'self' || !target
        ? centerOf(self.body, def)
        : { x: target.x, y: target.y + 1, z: target.z };
    const enemy = otherSlot(slot);
    const targetMech = mech(t, enemy);
    const victims = targetMech.alive ? [enemy] : [];
    if (p.selfDamage)
        victims.push(slot);
    for (const victim of victims) {
        const cap = combatantCapsule(mech(t, victim), defOf(t, victim));
        const q = closestPointOnAxis(origin, cap);
        const dist = Math.max(0, Math.hypot(q.x - origin.x, q.y - origin.y, q.z - origin.z) - cap.radius);
        let inside;
        if (p.shape === 'cone') {
            inside =
                victim !== slot &&
                    capsuleInCone(origin, aimDir(self.body), degToRad(p.angleDeg / 2), p.radius, cap);
        }
        else {
            inside = dist <= p.radius && dist >= p.innerRadius;
        }
        if (!inside)
            continue;
        if (p.lineOfSight && victim !== slot && dist > 1e-6) {
            const dir = vNormalize({ x: q.x - origin.x, y: q.y - origin.y, z: q.z - origin.z });
            const hit = t.world.castRayHit(origin, dir, dist + cap.radius);
            if (hit && hit.distance < dist)
                continue;
        }
        const v = mech(t, victim);
        if (p.executeBelow > 0 && victim !== slot && v.hp <= defOf(t, victim).hp * p.executeBelow) {
            applyDamage(t, {
                target: victim,
                source: slot,
                amount: v.hp,
                kind: 'execute',
                location: 'torso',
                rear: false,
                position: centerOf(v.body, defOf(t, victim)),
                direct: true,
            });
            continue;
        }
        const factor = 1 - (1 - p.minFactor) * Math.min(1, dist / p.radius);
        if (p.damage > 0)
            applyDamage(t, areaPacket(t, victim, slot, p.damage * factor, 'ability', origin));
        if (victim !== slot) {
            if (p.knockback > 0 || p.knockUp > 0)
                knockbackFrom(t, victim, slot, origin, p.knockback, p.knockUp);
            if (p.effect && !t.mods[victim].ccImmune) {
                refreshEffect(t, victim, p.effect.kind, slot, secondsToTicks(p.effect.seconds), modsToParams(p.effect.mods));
            }
            if (p.dot) {
                refreshEffect(t, victim, 'burn', slot, secondsToTicks(p.dot.seconds), {
                    dot: { damagePerSecond: p.dot.damagePerSecond, kind: 'burn' },
                });
            }
        }
    }
    for (const d of [...t.s.deployables]) {
        if (d.slot === slot || d.maxHp <= 0)
            continue;
        const dist = Math.hypot(d.position.x - origin.x, d.position.y - origin.y, d.position.z - origin.z);
        if (dist <= p.radius)
            damageDeployable(t, d, p.damage * (1 - (1 - p.minFactor) * (dist / p.radius)), slot);
    }
    emit(t, { t: 'explosion', slot, position: { ...origin }, radius: p.radius });
}
function runCc(t, slot, p) {
    const self = mech(t, slot);
    const ticks = secondsToTicks(p.seconds);
    for (const target of targetsOf(t, slot, p.target, p.radius, p.lineOfSight)) {
        if (t.mods[target].ccImmune)
            continue;
        switch (p.kind) {
            case 'stun':
                refreshEffect(t, target, 'stun', slot, ticks, { flags: ['stunned'] });
                break;
            case 'root':
                refreshEffect(t, target, 'root', slot, ticks, { flags: ['immobile'] });
                break;
            case 'slow':
                refreshEffect(t, target, 'slow', slot, ticks, { speedScale: p.strength });
                break;
            case 'emp':
                refreshEffect(t, target, 'emp', slot, ticks, {
                    flags: ['noBoost', 'noAbilities', 'noUltCharge'],
                });
                break;
            case 'heatLock':
                refreshEffect(t, target, 'heatLock', slot, ticks, { flags: ['heatLock'] });
                break;
            case 'knockback':
                knockbackFrom(t, target, slot, self.body.position, p.strength, 1);
                break;
            case 'knockUp':
                applyImpulse(t, target, slot, { x: 0, y: p.strength, z: 0 });
                break;
            case 'pull': {
                const v = mech(t, target);
                const dx = self.body.position.x - v.body.position.x;
                const dz = self.body.position.z - v.body.position.z;
                const len = Math.hypot(dx, dz) || 1;
                applyImpulse(t, target, slot, {
                    x: (dx / len) * p.strength,
                    y: 1,
                    z: (dz / len) * p.strength,
                });
                break;
            }
        }
    }
}
function runHeal(t, slot, p) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    if (p.seconds <= 0) {
        c.hp = Math.min(def.hp, c.hp + p.hp);
        c.shield = Math.min(def.shield, c.shield + p.shield);
        c.heat = Math.max(0, c.heat - p.heat);
        c.body.energy = Math.min(def.energy, c.body.energy + p.energy);
        return;
    }
    addEffect(t, slot, 'heal', slot, secondsToTicks(p.seconds), {
        hot: {
            hp: p.hp / p.seconds,
            shield: p.shield / p.seconds,
            heat: -p.heat / p.seconds,
            energy: p.energy / p.seconds,
        },
    });
}
function runStance(t, slot, p) {
    const c = mech(t, slot);
    const params = {
        ...modsToParams(p.mods),
        ...(p.parry
            ? { parry: { arcDeg: p.parry.arcDeg, stunTicks: secondsToTicks(p.parry.stunSeconds) } }
            : {}),
    };
    if (p.mode === 'toggle') {
        if (c.effects.some((e) => e.kind === p.kind)) {
            removeEffectsOfKind(t, slot, p.kind);
            return;
        }
        addEffect(t, slot, p.kind, slot, TOGGLE_TICKS, params);
        return;
    }
    refreshEffect(t, slot, p.kind, slot, secondsToTicks(p.seconds ?? 1), params);
}
function runBurst(t, slot, p, target) {
    weaponOf(t.ctx, p.weapon);
    const intervalTicks = Math.max(1, secondsToTicks(p.intervalSeconds));
    const effect = addEffect(t, slot, 'burst', slot, p.count * intervalTicks + 1, {
        burst: {
            weapon: p.weapon,
            left: p.count,
            intervalTicks,
            timer: intervalTicks,
            pattern: p.pattern,
            spreadDeg: p.spreadDeg,
            scatter: p.scatterMeters,
            target: target ? { ...target } : null,
        },
    });
    // The first round leaves on the activation tick.
    fireBurstShot(t, slot, effect);
    effect.params.burst.left--;
}
function runBeam(t, slot, p) {
    const weapon = weaponOf(t.ctx, p.weapon);
    const siphon = {
        hpPerSecond: p.siphon?.hpPerSecond ?? 0,
        energyPerSecond: p.siphon?.energyPerSecond ?? 0,
    };
    addEffect(t, slot, 'beam', slot, secondsToTicks(p.seconds), {
        beam: { weapon: p.weapon, ...siphon },
    });
    // The beam connects on the activation tick; the effect carries it for the rest.
    const c = mech(t, slot);
    const def = defOf(t, slot);
    beamTick(t, slot, weapon, aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction), aimDir(c.body), { x: c.body.position.x, y: c.body.position.y + def.capsule.height * 0.6, z: c.body.position.z }, { siphon: siphon.hpPerSecond > 0 || siphon.energyPerSecond > 0 ? siphon : null });
}
export function runPrimitive(t, slot, p, target, input) {
    switch (p.type) {
        case 'dash':
            runDash(t, slot, p, target, input);
            break;
        case 'cloak':
            addEffect(t, slot, 'cloak', slot, secondsToTicks(p.seconds), {
                cloak: { revealDistance: p.revealDistance, breakOnFire: p.breakOnFire },
                flags: ['cloaked'],
            });
            break;
        case 'deploy':
            runDeploy(t, slot, p, target);
            break;
        case 'buff':
            runBuff(t, slot, p);
            break;
        case 'aoe':
            runAoe(t, slot, p, target);
            break;
        case 'burst':
            runBurst(t, slot, p, target);
            break;
        case 'beam':
            runBeam(t, slot, p);
            break;
        case 'cc':
            runCc(t, slot, p);
            break;
        case 'heal':
            runHeal(t, slot, p);
            break;
        case 'stance':
            runStance(t, slot, p);
            break;
        case 'custom':
            throw new Error(`custom primitive "${p.id}" has no implementation`);
    }
}
//# sourceMappingURL=primitives.js.map